import { Box, IconButton, Typography, useTheme, Button, useMediaQuery, Snackbar, Alert, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import HelpDrawer from "./HelpDrawer";
import { useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ColorModeContext, tokens } from "../theme";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import DirectionsBikeIcon from "@mui/icons-material/DirectionsBike";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import StraightenIcon from '@mui/icons-material/Straighten';
import LanguageIcon from '@mui/icons-material/Language';
import PersonIcon from '@mui/icons-material/Person';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import FeedbackIcon from '@mui/icons-material/Feedback';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import api from '../api/client';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useWorkoutSession } from '../contexts/WorkoutSessionContext';
import FeedbackDialog from './FeedbackDialog';
import '../i18n/index';

// WhatsApp Business support line. wa.me wants the number with no "+", spaces
// or leading zeros, which is also exactly how it's dialable internationally.
const WHATSAPP_SUPPORT_URL = 'https://wa.me/40736873823';

// UUID v4 generator
function generateUUIDv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

const Topbar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { activeSessionData } = useWorkoutSession();
  const [units, setUnits] = useState(() => localStorage.getItem('units') || 'metric');
  const [langAnchorEl, setLangAnchorEl] = useState(null);
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language || 'en');
  const [currentSport, setCurrentSport] = useState('bike');
  const [uuid, setUuid] = useState(() => {
    let stored = localStorage.getItem('uuid');
    if (!stored) {
      stored = generateUUIDv4();
      localStorage.setItem('uuid', stored);
    }
    return stored;
  });
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  const [showUnitsNotification, setShowUnitsNotification] = useState(false);

  // Help drawer state (inlined so we can trigger from overflow menu).
  // The drawer loads its own content from /help — see HelpDrawer.jsx.
  const [helpOpen, setHelpOpen] = useState(false);

  // Feedback dialog state
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // Mobile overflow menu state
  const [overflowAnchorEl, setOverflowAnchorEl] = useState(null);

  useEffect(() => {
    localStorage.setItem('units', units);
  }, [units]);

  // Detect current sport from URL
  useEffect(() => {
    setCurrentSport(location.pathname.startsWith('/running') ? 'running' : 'bike');
  }, [location.pathname]);

  // Sync local state with i18n language (e.g., on mount or when i18n language changes externally)
  useEffect(() => {
    const handleLanguageChanged = (lng) => {
      setLanguage(lng);
    };
    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  // Send user session data to backend
  useEffect(() => {
    const sendUserSession = async () => {
      try {
        await api.post('/user-session', {
          uuid,
          language,
          units,
        });
      } catch (err) {
        // Optionally log or ignore
      }
    };
    sendUserSession();
  }, [uuid, language, units]);

  const handleToggleUnits = () => {
    const newUnits = units === 'metric' ? 'imperial' : 'metric';
    setUnits(newUnits);
    localStorage.setItem('units', newUnits);
    window.dispatchEvent(new CustomEvent('unitsChanged', { detail: newUnits }));
    if (isMobile) {
      setShowUnitsNotification(true);
    }
  };

  const handleToggleSport = (event, newSport) => {
    if (newSport === null) return;
    if (newSport === 'bike') {
      navigate('/bike');
    } else {
      navigate('/running');
    }
  };

  const handleLangClick = (event) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangClose = () => {
    setLangAnchorEl(null);
  };

  const handleLangSelect = (lang) => {
    i18n.changeLanguage(lang);
    setLanguage(lang);
    setLangAnchorEl(null);
  };

  const languageLabels = t('languages', { returnObjects: true });

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleOverflowOpen = (event) => {
    setOverflowAnchorEl(event.currentTarget);
  };

  const handleOverflowClose = () => {
    setOverflowAnchorEl(null);
  };

  const handleOverflowHelp = () => {
    handleOverflowClose();
    setHelpOpen(true);
  };

  const handleOverflowUnits = () => {
    handleOverflowClose();
    handleToggleUnits();
  };

  const handleOverflowLang = (event) => {
    handleOverflowClose();
    // Small delay so overflow menu closes before lang menu opens
    setTimeout(() => setLangAnchorEl(event.currentTarget), 50);
  };

  const handleOverflowFeedback = () => {
    handleOverflowClose();
    setFeedbackOpen(true);
  };

  const handleWhatsAppSupport = () => {
    window.open(WHATSAPP_SUPPORT_URL, '_blank', 'noopener,noreferrer');
  };

  const handleOverflowWhatsApp = () => {
    handleOverflowClose();
    handleWhatsAppSupport();
  };

  const hasFeedbackSession = !!activeSessionData?.analysis_session_id;

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      p={isMobile ? 1.5 : 2}
      sx={{
        backgroundColor: colors.grey[800],
        borderRadius: "8px",
        marginBottom: "20px"
      }}
    >
      <Box display="flex" alignItems="center" gap={2}>
        <Typography
          variant="h3"
          fontWeight="bold"
          onClick={() => navigate(currentSport === 'running' ? '/running' : '/bike')}
          sx={{
            fontFamily: "'Ubuntu', sans-serif",
            cursor: 'pointer',
            fontSize: isMobile ? '1.25rem' : '1.5rem',
            '&:hover': {
              opacity: 0.8
            }
          }}
        >
          <Box component="span" sx={{ color: colors.blueAccent[500] }}>mk</Box>
          <Box component="span" sx={{ color: colors.grey[100] }}>pace</Box>
        </Typography>

        {/* Sport Toggle */}
        <ToggleButtonGroup
          value={currentSport}
          exclusive
          onChange={handleToggleSport}
          size="small"
          sx={{
            ml: isMobile ? 1 : 2,
            '& .MuiToggleButton-root': {
              px: isMobile ? 0.75 : 1.5,
              py: isMobile ? 0.25 : 0.5,
              border: `1px solid ${colors.grey[700]}`,
              color: colors.grey[100],
              '&.Mui-selected': {
                backgroundColor: colors.blueAccent[700],
                color: colors.grey[100],
                '&:hover': { backgroundColor: colors.blueAccent[600] }
              },
              '&:hover': { backgroundColor: colors.grey[700] }
            }
          }}
        >
          <ToggleButton value="bike">
            <Tooltip title="Bike Analysis">
              <DirectionsBikeIcon sx={{ fontSize: isMobile ? "16px" : "20px" }} />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="running">
            <Tooltip title="Running Analysis">
              <DirectionsRunIcon sx={{ fontSize: isMobile ? "16px" : "20px" }} />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box display="flex" alignItems="center" gap={isMobile ? 0.5 : 1}>
        {isMobile ? (
          /* ---- Mobile: single "..." overflow button ---- */
          <>
            <IconButton onClick={handleOverflowOpen} size="small">
              <MoreHorizIcon />
            </IconButton>
            <Menu
              anchorEl={overflowAnchorEl}
              open={Boolean(overflowAnchorEl)}
              onClose={handleOverflowClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={handleOverflowHelp}>
                <ListItemIcon><HelpOutlineIcon fontSize="small" /></ListItemIcon>
                <ListItemText>{t('help', 'Help')}</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleOverflowUnits}>
                <ListItemIcon><StraightenIcon fontSize="small" /></ListItemIcon>
                <ListItemText>
                  {units === 'metric' ? t('unitsMetric') : t('unitsImperial')}
                </ListItemText>
              </MenuItem>
              <MenuItem onClick={handleOverflowLang}>
                <ListItemIcon><LanguageIcon fontSize="small" /></ListItemIcon>
                <ListItemText>{languageLabels[language] || language}</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleOverflowFeedback} disabled={!hasFeedbackSession}>
                <ListItemIcon><FeedbackIcon fontSize="small" /></ListItemIcon>
                <ListItemText>{t('feedback', 'Feedback')}</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleOverflowWhatsApp}>
                <ListItemIcon><WhatsAppIcon fontSize="small" sx={{ color: '#25D366' }} /></ListItemIcon>
                <ListItemText>{t('whatsappSupport', 'WhatsApp Support')}</ListItemText>
              </MenuItem>
            </Menu>
          </>
        ) : (
          /* ---- Desktop: individual buttons ---- */
          <>
            <IconButton onClick={() => setHelpOpen(true)} sx={{ ml: 1 }} title="Help / Welcome">
              <HelpOutlineIcon />
            </IconButton>
            <IconButton onClick={colorMode.toggleColorMode}>
              {theme.palette.mode === "dark" ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
            </IconButton>
            <IconButton onClick={handleToggleUnits}>
              <StraightenIcon />
            </IconButton>
            <Typography variant="body2" color={colors.grey[300]} sx={{ ml: 0.5 }}>
              {units === 'metric' ? t('unitsMetric') : t('unitsImperial')}
            </Typography>
            <IconButton onClick={handleLangClick}>
              <LanguageIcon />
            </IconButton>
            <Typography variant="body2" color={colors.grey[300]} sx={{ ml: 0.5 }}>
              {languageLabels[language]}
            </Typography>
            <Tooltip title={hasFeedbackSession ? t('feedback', 'Feedback') : t('feedbackNoSession', 'Open a workout to leave feedback')}>
              <span>
                <IconButton onClick={() => setFeedbackOpen(true)} disabled={!hasFeedbackSession}>
                  <FeedbackIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('whatsappSupport', 'WhatsApp Support')}>
              <IconButton onClick={handleWhatsAppSupport}>
                <WhatsAppIcon sx={{ color: '#25D366' }} />
              </IconButton>
            </Tooltip>
          </>
        )}

        {/* Language picker menu (shared by desktop + mobile) */}
        <Menu anchorEl={langAnchorEl} open={Boolean(langAnchorEl)} onClose={handleLangClose}>
          {Object.entries(languageLabels).map(([lang, label]) => (
            <MenuItem key={lang} selected={language === lang} onClick={() => handleLangSelect(lang)}>
              {label}
            </MenuItem>
          ))}
        </Menu>

        {/* Authentication UI */}
        <Box sx={{ ml: isMobile ? 0.5 : 3, display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 1 }}>
          {isAuthenticated ? (
            <>
              {user?.is_admin && !isMobile && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/admin')}
                  sx={{ mr: 1 }}
                >
                  Admin
                </Button>
              )}
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                onClick={() => navigate('/profile')}
                sx={{
                  cursor: 'pointer',
                  p: 1,
                  borderRadius: 1,
                  '&:hover': { backgroundColor: colors.grey[700] }
                }}
              >
                <PersonIcon sx={{ color: colors.greenAccent[500] }} />
                {!isMobile && (
                  <Typography variant="body2" color={colors.grey[300]}>
                    {user?.email}
                  </Typography>
                )}
              </Box>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate('/login', { state: { from: location.pathname + location.search } })}
                sx={{
                  mr: isMobile ? 0.5 : 1,
                  minWidth: isMobile ? 'auto' : undefined,
                  px: isMobile ? 0.75 : undefined,
                  whiteSpace: 'nowrap',
                  fontSize: isMobile ? '0.8rem' : undefined,
                  color: theme.palette.mode === 'dark' ? 'inherit' : colors.grey[100],
                  borderColor: theme.palette.mode === 'dark' ? 'inherit' : colors.grey[100]
                }}
              >
                {t('login')}
              </Button>
              {!isMobile && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate('/register')}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t('signUp')}
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Help drawer */}
      <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />

      {/* Feedback dialog */}
      <FeedbackDialog
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        workoutData={activeSessionData}
      />

      {/* Units change notification for mobile */}
      <Snackbar
        open={showUnitsNotification}
        autoHideDuration={2000}
        onClose={() => setShowUnitsNotification(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowUnitsNotification(false)}
          severity="info"
          sx={{
            width: '100%',
            backgroundColor: colors.blueAccent[700],
            color: colors.grey[100]
          }}
        >
          {units === 'metric' ? t('unitsMetric') : t('unitsImperial')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Topbar;
